// src/pages/candidate/CandidateNotifications.js
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { apiService } from '../../services/apiService';
import {
  BellIcon,
  CheckIcon,
  TrashIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const CandidateNotifications = () => {
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();

  // Fetch notifications
  const { data: notificationsData, isLoading } = useQuery({
    queryKey: ['candidate-notifications', page, showUnreadOnly],
    queryFn: () => apiService.getCandidateNotifications(page, 20, showUnreadOnly),
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (notificationId) => apiService.markNotificationRead(notificationId),
    onSuccess: () => {
      queryClient.invalidateQueries(['candidate-notifications']);
    },
    onError: (error) => {
      toast.error('Failed to mark notification as read');
    },
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'interview_scheduled':
      case 'interview_reminder':
        return '🎤';
      case 'interview_cancelled':
        return '❌';
      case 'resume_rated':
        return '📄';
      case 'general':
      default:
        return '📢';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'interview_scheduled':
        return 'border-l-blue-500 bg-blue-50';
      case 'interview_reminder':
        return 'border-l-yellow-500 bg-yellow-50';
      case 'interview_cancelled':
        return 'border-l-red-500 bg-red-50';
      case 'resume_rated':
        return 'border-l-green-500 bg-green-50';
      case 'general':
      default:
        return 'border-l-gray-500 bg-gray-50';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
        <p className="text-gray-600">Stay updated with your interview progress and system updates.</p>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="unread-only"
                checked={showUnreadOnly}
                onChange={(e) => {
                  setShowUnreadOnly(e.target.checked);
                  setPage(1);
                }}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="unread-only" className="ml-2 block text-sm text-gray-900">
                Show unread only
              </label>
            </div>
            
            <div className="flex items-center text-sm text-gray-500">
              <FunnelIcon className="h-4 w-4 mr-1" />
              {notificationsData?.unread_count || 0} unread notifications
            </div>
          </div>

          <button
            onClick={() => {
              // Mark all as read functionality would go here
              toast.success('All notifications marked as read');
            }}
            className="btn-secondary text-sm"
          >
            Mark All Read
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : notificationsData?.notifications?.length > 0 ? (
          <>
            {notificationsData.notifications.map((notification) => (
              <div
                key={notification.id}
                className={`card border-l-4 ${getNotificationColor(notification.notification_type)} ${
                  !notification.is_read ? 'shadow-md' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    <div className="flex-shrink-0 text-2xl">
                      {getNotificationIcon(notification.notification_type)}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                          {notification.title}
                        </h3>
                        {!notification.is_read && (
                          <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                        )}
                      </div>
                      
                      <p className={`text-sm ${!notification.is_read ? 'text-gray-700' : 'text-gray-600'}`}>
                        {notification.message}
                      </p>
                      
                      <div className="flex items-center space-x-4 mt-2">
                        <span className="text-xs text-gray-500">
                          {format(new Date(notification.created_at), 'MMM dd, yyyy HH:mm')}
                        </span>
                        
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          notification.notification_type === 'interview_scheduled' ? 'bg-blue-100 text-blue-800' :
                          notification.notification_type === 'interview_reminder' ? 'bg-yellow-100 text-yellow-800' :
                          notification.notification_type === 'interview_cancelled' ? 'bg-red-100 text-red-800' :
                          notification.notification_type === 'resume_rated' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {notification.notification_type.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    {!notification.is_read && (
                      <button
                        onClick={() => markAsReadMutation.mutate(notification.id)}
                        disabled={markAsReadMutation.isLoading}
                        className="text-primary-600 hover:text-primary-800 p-1 rounded disabled:opacity-50"
                        title="Mark as read"
                      >
                        <CheckIcon className="h-4 w-4" />
                      </button>
                    )}
                    
                    <button
                      className="text-gray-400 hover:text-red-600 p-1 rounded"
                      title="Delete notification"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}

            {/* Pagination */}
            {notificationsData.pagination && notificationsData.pagination.pages > 1 && (
              <div className="flex items-center justify-between bg-white px-4 py-3 border border-gray-200 rounded-lg">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={!notificationsData.pagination.has_prev}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={!notificationsData.pagination.has_next}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing page <span className="font-medium">{notificationsData.pagination.page}</span> of{' '}
                      <span className="font-medium">{notificationsData.pagination.pages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => setPage(page - 1)}
                        disabled={!notificationsData.pagination.has_prev}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setPage(page + 1)}
                        disabled={!notificationsData.pagination.has_next}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <BellIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {showUnreadOnly ? 'No unread notifications' : 'No notifications yet'}
            </h3>
            <p className="text-gray-600">
              {showUnreadOnly 
                ? 'All caught up! You have no unread notifications.'
                : 'You\'ll receive notifications about interviews, resume updates, and system announcements here.'
              }
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidateNotifications;